import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLibraryStore, isOwnPoi } from "../store/libraryStore";
import { useUIStore } from "../store/uiStore";
import { useTripsListStore } from "../store/tripsListStore";
import {
  usePoiFilters,
  type SortMode,
  type MapBounds,
} from "../hooks/usePoiFilters";
import { LibraryPoiList } from "../components/LibraryPoiList";
import { MapView } from "../components/MapView";
import { Button } from "../components/Button";
import { POIEditorModal } from "../components/POIEditorModal";
import { PoiFilterBar } from "../components/PoiFilterBar";
import { CollectionPickerModal } from "../components/CollectionPickerModal";
import { CollectionEditorModal } from "../components/CollectionEditorModal";
import { useCollectionsStore } from "../store/collectionsStore";
import { api } from "../services/api";
import { useAuthStore } from "../store/authStore";

export function LibraryPage() {
  const navigate = useNavigate();
  const pois = useLibraryStore((s) => s.pois);
  const tags = useLibraryStore((s) => s.tags);
  const categoryTags = useLibraryStore((s) => s.categoryTags);
  const currentUserId = useAuthStore((s) => s.userId);
  // The library page shows only the caller's own POIs; POIs made visible
  // through a shared trip appear in the trip sidebar/map instead.
  const ownPois = useMemo(() => pois.filter(isOwnPoi), [pois]);
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const [flyToTrigger, setFlyToTrigger] = useState(0);

  // Map bounds filter state
  const [boundsFilterEnabled, setBoundsFilterEnabled] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);

  // Selection mode for bulk operations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPoiIds, setSelectedPoiIds] = useState<Set<string>>(new Set());
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const { collections, createCollection } = useCollectionsStore();

  // Trip POI IDs map for filtering (tripId -> Set<poiId>)
  const trips = useTripsListStore((s) => s.trips);
  const [tripPoiIdsMap, setTripPoiIdsMap] = useState<Map<string, Set<string>>>(
    new Map(),
  );

  // Build collection POI map for filtering
  const collectionPoiMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const collection of collections) {
      map.set(collection.id, new Set(collection.poiIds));
    }
    return map;
  }, [collections]);

  // Collection options for filter dropdown
  const collectionOptions = useMemo(() => {
    return collections.map((c) => ({ id: c.id, name: c.name }));
  }, [collections]);

  // Trip options for filter dropdown
  const tripOptions = useMemo(() => {
    return trips.map((t) => ({ id: t.id, name: t.name }));
  }, [trips]);

  // Target POI for proximity sorting (shared with trip board)
  const targetPoiId = useUIStore((s) => s.targetPoiId);
  const setTargetPoiId = useUIStore((s) => s.setTargetPoiId);
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);

  // Track previous sort mode to restore when target is cleared
  const [prevSortMode, setPrevSortMode] =
    useState<Exclude<SortMode, "distance">>("alpha");

  // Filters
  const {
    search,
    sortMode,
    sortAscending,
    tagMatchMode,
    tagFilters,
    sourceFilters,
    collectionFilters,
    tripFilters,
    setSearch,
    setSortMode,
    setSortAscending,
    setTagMatchMode,
    toggleTag,
    toggleSource,
    toggleCollection,
    toggleTrip,
    clearAllFilters,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    activeFilterCounts,
    maximumDistanceKm,
    setMaximumDistanceKm,
  } = usePoiFilters(ownPois, {
    boundsFilter: {
      enabled: boundsFilterEnabled,
      bounds: mapBounds,
    },
    distanceTarget: {
      targetPoiId,
      allPois: ownPois,
    },
    collectionPoiMap,
    tripPoiIdsMap,
    tagContext: { tags, categoryTags, currentUserId },
  });

  // Load trip POI IDs when trip filters change
  useEffect(() => {
    const tripIds = [...tripFilters.keys()];
    if (tripIds.length === 0) return;

    // Load POI IDs for any trips we don't have yet
    const missingTrips = tripIds.filter((id) => !tripPoiIdsMap.has(id));
    if (missingTrips.length === 0) return;

    let cancelled = false;
    Promise.all(
      missingTrips.map((tripId) =>
        api.getTripPoiIds(tripId).then((ids) => ({ tripId, ids })),
      ),
    )
      .then((results) => {
        if (cancelled) return;
        setTripPoiIdsMap((prev) => {
          const next = new Map(prev);
          for (const { tripId, ids } of results) {
            next.set(tripId, new Set(ids));
          }
          return next;
        });
      })
      .catch((err) => {
        console.error("Failed to load trip POI IDs:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [tripFilters, tripPoiIdsMap]);

  // Auto-switch to distance sort when target is set
  useEffect(() => {
    if (targetPoiId) {
      if (sortMode !== "distance") {
        setPrevSortMode(sortMode as Exclude<SortMode, "distance">);
        setSortMode("distance");
      }
      return;
    }

    if (sortMode === "distance") {
      setSortMode(prevSortMode);
    }
  }, [prevSortMode, setSortMode, sortMode, targetPoiId]);

  // Get target POI for badge display
  const targetPoi = useMemo(() => {
    if (!targetPoiId) return null;
    return ownPois.find((p) => p.id === targetPoiId) ?? null;
  }, [ownPois, targetPoiId]);

  // Clear target when leaving page
  useEffect(() => {
    return () => {
      setTargetPoiId(null);
    };
  }, [setTargetPoiId]);

  const handleSelectPoi = (id: string) => {
    // Always select and fly to the POI
    setSelectedPoiId(id);
    setFlyToTrigger((t) => t + 1);
  };

  const handleEditPoi = (id: string) => {
    openPOIEditor("edit", id);
  };

  const handleCreatePoi = () => {
    openPOIEditor("create");
  };

  const handleMapSelectPoi = (id: string | null) => {
    setSelectedPoiId(id);
  };

  // Selection mode handlers
  const handleToggleSelection = (poiId: string) => {
    setSelectedPoiIds((prev) => {
      const next = new Set(prev);
      if (next.has(poiId)) {
        next.delete(poiId);
      } else {
        next.add(poiId);
      }
      return next;
    });
  };

  const handleExitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedPoiIds(new Set());
  };

  const handleSelectAll = () => {
    setSelectedPoiIds(new Set(filteredPOIs.map((p) => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedPoiIds(new Set());
  };

  const handleCreateCollection = async (data: {
    name: string;
    description?: string;
    visibility: "private" | "public" | "shared";
  }) => {
    await createCollection(data.name, data.description, data.visibility);
  };

  return (
    <div className="h-screen bg-page flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-border shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/")}
              variant="icon"
              icon="arrow_back"
              title="Back to trips"
              aria-label="Back to trips"
            />
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-2xl text-accent">
                location_on
              </span>
              <h1 className="text-lg font-bold text-primary tracking-tight">
                POI Library
              </h1>
            </div>
          </div>

          {/* Actions moved to sidebar footer */}
          <div className="w-1" />
        </div>
      </header>

      {/* Selection Mode Toolbar */}
      {selectionMode && (
        <div className="bg-amber-50 border-b border-amber-200 shrink-0">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-semibold text-amber-800">
                {selectedPoiIds.size} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                >
                  Select all ({filteredPOIs.length})
                </button>
                {selectedPoiIds.size > 0 && (
                  <button
                    onClick={handleDeselectAll}
                    className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowCollectionPicker(true)}
              disabled={selectedPoiIds.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold border-none cursor-pointer transition-all hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-lg">
                folder_special
              </span>
              Add to Collection
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - List */}
        <div className="w-96 shrink-0 flex flex-col bg-white border-r border-border">
          {/* Header with title and action buttons */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h2 className="text-lg font-semibold text-primary">POI Library</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={handleCreatePoi}
                className="size-8 flex items-center justify-center rounded-lg text-secondary hover:bg-slate-100 hover:text-primary transition-colors"
                title="New POI"
              >
                <span className="material-symbols-outlined text-xl">add</span>
              </button>
              <button
                onClick={() =>
                  selectionMode
                    ? handleExitSelectionMode()
                    : setSelectionMode(true)
                }
                className={`size-8 flex items-center justify-center rounded-lg transition-colors ${
                  selectionMode
                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                    : "text-secondary hover:bg-slate-100 hover:text-primary"
                }`}
                title={selectionMode ? "Cancel Selection" : "Select POIs"}
              >
                <span className="material-symbols-outlined text-xl">
                  checklist
                </span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="px-4 pb-4 border-b border-border">
            <PoiFilterBar
              search={search}
              sortMode={sortMode}
              sortAscending={sortAscending}
              tagFilters={tagFilters}
              allTags={allTags}
              onSearchChange={setSearch}
              onSortModeChange={setSortMode}
              onSortAscendingChange={setSortAscending}
              onToggleTag={toggleTag}
              tagMatchMode={tagMatchMode}
              onTagMatchModeChange={setTagMatchMode}
              sourceFilters={sourceFilters}
              allSourceTexts={allSourceTexts}
              onToggleSource={toggleSource}
              collectionFilters={collectionFilters}
              collections={collectionOptions}
              onToggleCollection={toggleCollection}
              tripFilters={tripFilters}
              trips={tripOptions}
              onToggleTrip={toggleTrip}
              onClearAllFilters={clearAllFilters}
              activeFilterCounts={activeFilterCounts}
              boundsFilterEnabled={boundsFilterEnabled}
              onBoundsFilterChange={setBoundsFilterEnabled}
              targetPoi={targetPoi}
              onClearTarget={() => setTargetPoiId(null)}
              maximumDistanceKm={maximumDistanceKm}
              onMaximumDistanceChange={setMaximumDistanceKm}
              filteredCount={filteredPOIs.length}
              totalCount={ownPois.length}
            />
          </div>

          {/* POI List */}
          <LibraryPoiList
            pois={filteredPOIs}
            selectedPoiId={selectedPoiId}
            onSelectPoi={handleSelectPoi}
            onEditPoi={handleEditPoi}
            distanceMap={distanceMap}
            showDistance={!!targetPoiId}
            targetPoiId={targetPoiId}
            onSetTarget={setTargetPoiId}
            selectionMode={selectionMode}
            selectedIds={selectedPoiIds}
            onToggleSelection={handleToggleSelection}
          />
        </div>

        {/* Right Panel - Map */}
        <div className="flex-1 flex flex-col p-4">
          <MapView
            pois={filteredPOIs}
            tags={tags}
            selectedPoiId={selectedPoiId}
            flyToTrigger={flyToTrigger}
            onSelectPoi={handleMapSelectPoi}
            onBoundsChange={setMapBounds}
          />
        </div>
      </div>

      {/* POI Editor Modal */}
      <POIEditorModal />

      {/* Collection Picker Modal */}
      <CollectionPickerModal
        open={showCollectionPicker}
        onClose={() => {
          setShowCollectionPicker(false);
          // Optionally exit selection mode after adding to collection
          // handleExitSelectionMode();
        }}
        poiIds={[...selectedPoiIds]}
        onCreateNew={() => setShowCreateCollection(true)}
      />

      {/* Create Collection Modal */}
      <CollectionEditorModal
        open={showCreateCollection}
        onClose={() => setShowCreateCollection(false)}
        onSave={handleCreateCollection}
      />
    </div>
  );
}
